import { useState } from 'react'
import type { SeriesResponseDto } from '@/api/generated/api'
import type {
  SeriesWithRelations,
  SeriesMoveWithRelations,
} from '@/types/api-extensions'

interface SeriesCardProps {
  series: SeriesResponseDto
}

export function SeriesCard({ series }: SeriesCardProps) {
  const [imageError, setImageError] = useState(false)

  // Get thumbnail from first move in series if available
  const seriesWithRelations = series as SeriesWithRelations
  const thumbnail = getFirstMoveThumbnail(seriesWithRelations)

  const handleImageError = () => {
    setImageError(true)
  }

  return (
    <div className="overflow-hidden">
      {/* Full-width thumbnail */}
      <div className="relative aspect-video w-full overflow-hidden bg-muted">
        {thumbnail && !imageError ? (
          <img
            src={thumbnail}
            alt={series.name}
            className="h-full w-full object-cover"
            onError={handleImageError}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-200">
            <span className="text-5xl">📚</span>
          </div>
        )}
      </div>
      {/* Text content below thumbnail */}
      <div className="mt-2">
        <h4 className="truncate text-sm font-medium text-black">
          {series.name}
        </h4>
        {series.description && (
          <p className="mt-0.5 line-clamp-2 text-xs text-[#666666]">
            {series.description}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * Extract thumbnail URL from first move in series
 */
function getFirstMoveThumbnail(
  series: SeriesWithRelations
): string | undefined {
  if (!series.seriesMoves || series.seriesMoves.length === 0) {
    return undefined
  }

  // Get first move with thumbnail from series
  const activeMoves = series.seriesMoves.filter(
    (sm: SeriesMoveWithRelations) => sm.move?.thumbnailUrl
  )

  for (const seriesMove of activeMoves) {
    if (seriesMove.move?.thumbnailUrl) {
      return seriesMove.move.thumbnailUrl
    }
  }

  return undefined
}
